import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Chip, FormControl, Grid, IconButton, makeStyles, MenuItem, Select, Tooltip, Typography } from '@material-ui/core';
import { Done, ErrorOutline } from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';

import { WorkspaceMainFileType, WorkspaceProductType } from '../../models/Workspace';
import Table from '../common/Table';
import TableEmailCell from '../common/TableEmailCell';
import StatusCell from '../creators/StatusCell';

const useStyles = makeStyles((theme) => ({
  alertIconGreen: {
    backgroundColor: 'inherit !important',
    color: '#FFF !important',
  },
  alertIconWhite: {
    backgroundColor: '#0000 !important',
    color: 'inherit !important',
  },
  clinicCode: {
    color: '#172B4D',
    fontWeight: 600,
  },
  clinicCodeContainer: {
    color: '#999999',
    fontSize: '16px',
    fontWeight: 400,
  },
  clinicUploadTooltip: {
    color: '#FFF',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '23px',
  },
  fieldsActionWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'end',
    marginTop: '4px',
  },
  fieldsContainer: {
    marginTop: '8px',
  },
  fieldsDataContainer: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'start',
  },
  fieldsIcon: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    height: '20px',
    marginLeft: '4px',
    width: '20px',
  },
  fieldsInput: {
    '&:focus': {
      border: '1px solid #E0E0E0',
    },
    '&:focus-visible': {
      outline: 'none',
    },
    border: '1px solid #E0E0E0',
    borderRadius: '4px',
    display: 'block',
    fontSize: '14px',
    padding: '8px 12px',
    width: '-webkit-fill-available',
  },
  fieldsLabel: {
    color: '#172B4D',
    display: 'block',
    fontWeight: 'medium',
    marginBottom: 0,
  },
  fieldsValue: {
    '&:hover': {
      color: theme.palette.primary.main,
    },
    color: '#172B4D',
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: 0,
  },
  selectContainer: {
    '& .MuiOutlinedInput-input': {
      padding: '4px 12px',
    },
  },
  typographyTitle: {
    color: '#172B4D',
    fontSize: '14px',
    fontWeight: 'bold',
    lineHeight: '16px',
    marginBottom: '8px',
    marginTop: '8px',
    textAlign: 'left',
  },
  workspaceContainer: {
    display: 'flex',
    flexDirection: 'Column',
    gap: '24px',
  },
}));

function getColumns({ onInvitedClick }) {
  return [
    { label: 'Administrator', name: 'name', sort: true },
    {
      label: 'Email Address',
      name: 'email',
      options: {
        customBodyRender: (value, tableMeta) => {
          return <TableEmailCell value={value} onClick={() => true} />;
        },
      },
    },
    {
      label: 'Telephone',
      name: 'phoneNumber',
      sort: true,
    },
    {
      label: 'Admin Status',
      name: 'status',
      options: {
        customBodyRender: (value, meta) => {
          const creatorData = { email: meta.rowData[1], id: meta.rowData[4], name: meta.rowData[0], phoneNumber: meta.rowData[2] };
          return <StatusCell value={value} onInvitedClick={() => onInvitedClick(creatorData)} />;
        },
      },
    },
    {
      label: '',
      name: 'id',
      options: { display: false },
    },
  ];
}

const tooltipCopy = {
  allowed: (
    <span>
      Clinic upload for this workspace is allowed. The patient could request the clinic to upload the required files on behalf the patient.
      <br />
      (click the button again to not allow the clinic upload)
    </span>
  ),
  notAllowed: (
    <span>
      Clinic upload for this workspace is not allowed. The patient couldn&lsquo;t request the clinic to upload the required files.
      <br />
      (click the button again to allow clinic upload)
    </span>
  ),
};

const canRequestBeforePayCopy = {
  allowed: (
    <span>
      Patient allowed to request a report before completing the payment.
      <br />
      (click the button again to make the patient complete the payment before make a request)
    </span>
  ),
  notAllowed: (
    <span>
      Patient should make a payment before request a video report.
      <br />
      (click the button again to allow patient request video report before completing the payment)
    </span>
  ),
};

const creatorModeCopy = {
  allowed: (
    <span>
      Administrator has access to upload materials in resource library
      <br />
      (click the button again to disable the access)
    </span>
  ),
  notAllowed: (
    <span>
      Administrator has no access to upload materials in resource library
      <br />
      (click the button again to enable the access)
    </span>
  ),
};

const radiologyReportRequiredCopy = {
  allowed: (
    <span>
      Patient is required to upload radiology report before request a video report.
      <br />
      (click the button again to make the radiology report optional)
    </span>
  ),
  notAllowed: (
    <span>
      Patient is not required to upload radiology report before request a video report.
      <br />
      (click the button again to make the radiology report required)
    </span>
  ),
};

const sendEmailsToAdminsCopy = {
  doNotSend: (
    <span>
      No admin will receive notification emails when an order is placed. You still can add an specific admin email to the list of additional notification emails.
      <br />
      (click the button again to enable notification emails to admins)
    </span>
  ),
  send: (
    <span>
      Admins will receive notification emails when an order is placed.
      <br />
      (click the button again to disable this option)
    </span>
  ),
};

const Administrators = ({
  workspaceName,
  workspaceId,
  workspaceCreatorMode,
  workspaceRadiologyReportRequired,
  sendPlacedOrdersToAdmins,
  workspaceDiscount,
  canRequestBeforePay,
  onInvitedClick,
  onUserClick,
  onCanRequestBeforePayClick,
  onCreatorModeChange,
  onCriteriaChange,
  onUpdateDiscount,
  onRadiologyReportRequiredChange,
  onSendPlacedOrdersToAdminsChange,
  admins,
  onAddAdmin,
  total,
  discounts,
  onUpdateSubdomain,
  onUpdateLandingUrl,
  workspaceLandingUrl,
  workspaceSubdomain,
  isMediphanyWorkspace,
  mainFileType,
  onUpdateMainFileType,
  workspace,
}) => {
  const [administrators, setAdministrators] = useState(admins);
  const [landingUrl, setLandingUrl] = useState('');
  const [landingUrlEdit, setLandingUrlEdit] = useState(false);
  const [subdomain, setSubdomain] = useState('');
  const [subdomainEdit, setSubdomainEdit] = useState(false);

  const tableRef = useRef(undefined);
  const classes = useStyles();

  useEffect(() => {
    setAdministrators(admins);
  }, [admins]);

  useEffect(() => {
    if (workspaceId && tableRef?.current?.changePage) {
      tableRef.current.changePage(0);
    }
  }, [workspaceId]);

  const columns = getColumns({ onInvitedClick });
  const defaultOptions = {
    onTableChange: (action, tableState) => {
      const criteria = {};
      switch (action) {
        case 'sort':
          criteria.orderType = tableState.sortOrder.direction.toUpperCase();
          criteria.orderField = tableState.sortOrder.name;
          break;
        case 'changePage':
          criteria.page = tableState.page + 1;
          break;
        case 'changeRowsPerPage':
          criteria.limit = tableState.rowsPerPage;
          tableOptions.rowsPerPage = tableState.rowsPerPage;
          break;
        default:
          return;
      }
      onCriteriaChange(criteria);
    },
    serverSide: true,
  };

  const handleUpdateDiscount = (e) => {
    if (onUpdateDiscount) onUpdateDiscount(workspaceId, e.target.value);
  };

  const handleUpdateMainFileType = (e) => {
    if (onUpdateMainFileType) onUpdateMainFileType(workspaceId, e.target.value);
  };

  const handleRemoveDiscount = () => {
    if (onUpdateDiscount) onUpdateDiscount(workspaceId, null);
  };

  const handleCanRequestBeforePayClick = () => {
    if (onCanRequestBeforePayClick) onCanRequestBeforePayClick(workspaceId, !canRequestBeforePay);
  };

  const handleCreatorModeChange = () => {
    if (onCreatorModeChange) onCreatorModeChange(workspaceId, !workspaceCreatorMode);
  };

  const handleRadiologyReportRequiredChange = () => {
    if (onRadiologyReportRequiredChange) onRadiologyReportRequiredChange(workspaceId, !workspaceRadiologyReportRequired);
  };

  const handleSendPlacedOrdersToAdminsChange = () => {
    if (onSendPlacedOrdersToAdminsChange) onSendPlacedOrdersToAdminsChange(workspaceId, !sendPlacedOrdersToAdmins);
  };

  const handleSubdomainChange = useCallback((e) => {
    setSubdomain(e.target.value);
  }, []);

  const handleResetSubdomain = () => {
    setSubdomain(subdomain);
    setSubdomainEdit(false);
  };

  const handleUpdateSubdomain = () => {
    if (onUpdateSubdomain) onUpdateSubdomain(workspaceId, subdomain);
  };

  const handleLandingUrlChange = useCallback((e) => {
    setLandingUrl(e.target.value);
  }, []);

  const handleUpdateLandingUrl = () => {
    if (onUpdateLandingUrl) onUpdateLandingUrl(workspaceId, landingUrl);
  };

  const handleResetLandingUrl = () => {
    setLandingUrl(workspaceLandingUrl);
    setLandingUrlEdit(false);
  };

  const handleLandingUrlEditChange = (state) => {
    setLandingUrlEdit(state);
  };

  const handleSubdomainEditChange = (state) => {
    setSubdomainEdit(state);
  };

  const [tableOptions, setTableOptions] = useState(defaultOptions);
  useEffect(() => {
    setTableOptions({
      ...tableOptions,
      count: total,
      onRowSelectionChange: (currentRowsSelected, allRowsSelected, rowsSelected) => {
        const selectedAdmin = administrators[currentRowsSelected[0].index];
        if (selectedAdmin?.status !== 'Active' && selectedAdmin?.status !== 'Disabled') return;
        onUserClick({ status: selectedAdmin.status, userId: selectedAdmin.id });
      },
    });
  }, [total, administrators]);

  useEffect(() => {
    setLandingUrlEdit(false);
    setLandingUrl(workspaceLandingUrl);
  }, [workspaceId, workspaceLandingUrl]);

  useEffect(() => {
    setSubdomainEdit(false);
    setSubdomain(workspaceSubdomain);
  }, [workspaceId, workspaceSubdomain]);

  const managedLandingUrl = useMemo(() => {
    if (!workspaceLandingUrl) return '';

    const lowerCaseLandingUrl = workspaceLandingUrl.toLowerCase();
    if (lowerCaseLandingUrl.includes('http://') || lowerCaseLandingUrl.includes('https://')) return workspaceLandingUrl;
    return `https://${workspaceLandingUrl}`;
  }, [workspaceLandingUrl]);

  const managedWorkspaceSubdomain = useMemo(() => {
    const { protocol, host } = window.location;
    return { url: `${protocol}//${workspaceSubdomain}.${host}`, view: `${workspaceSubdomain}.${host}` };
  }, [workspaceSubdomain]);

  return (
    <Grid container className={classes.workspaceContainer}>
      <Grid item container>
        <Typography variant="h4" align="left">
          {workspaceName}
        </Typography>
      </Grid>
      <Grid item container>
        <Grid container justify="flex-end">
          <Grid item style={{ flexGrow: 1, marginTop: '8px', paddingRight: '8px', textAlign: 'left' }}>
            <div className={classes.fieldsContainer}>
              <Typography className={classes.fieldsLabel} variant="caption">
                Landing URL
              </Typography>

              {landingUrlEdit ? (
                <>
                  <input className={classes.fieldsInput} onChange={handleLandingUrlChange} type="text" value={landingUrl} placeholder="Example: landingurl.com/workspace" />
                  <div className={classes.fieldsActionWrapper}>
                    <Button onClick={handleResetLandingUrl} color="primary" style={{ marginRight: '4px' }} variant="outlined" size="small">
                      Cancel
                    </Button>
                    <Button onClick={handleUpdateLandingUrl} disabled={landingUrl === workspaceLandingUrl} color="primary" variant="contained" size="small">
                      Save
                    </Button>
                  </div>
                </>
              ) : (
                <div className={classes.fieldsDataContainer}>
                  {workspaceLandingUrl ? (
                    <a href={managedLandingUrl} target="blank" className={classes.fieldsValue}>
                      {workspaceLandingUrl}
                    </a>
                  ) : (
                    <span className={classes.fieldsValue}>-</span>
                  )}

                  <EditIcon className={classes.fieldsIcon} onClick={() => handleLandingUrlEditChange(true)} />
                </div>
              )}
            </div>

            {isMediphanyWorkspace ? null : (
              <div className={classes.fieldsContainer}>
                <Typography className={classes.fieldsLabel} variant="caption">
                  Login URL
                </Typography>

                {subdomainEdit ? (
                  <>
                    <input className={classes.fieldsInput} onChange={handleSubdomainChange} type="text" value={subdomain} placeholder="Type the subdomain" />
                    <div className={classes.fieldsActionWrapper}>
                      <Button onClick={handleResetSubdomain} color="primary" style={{ marginRight: '4px' }} variant="outlined" size="small">
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateSubdomain} disabled={subdomain === workspaceSubdomain || !subdomain} color="primary" variant="contained" size="small">
                        Save
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className={classes.fieldsDataContainer}>
                    {workspaceSubdomain ? (
                      <a href={managedWorkspaceSubdomain.url} target="_blank" rel="noreferrer" className={classes.fieldsValue}>
                        {managedWorkspaceSubdomain.view}
                      </a>
                    ) : (
                      <span className={classes.fieldsValue}>-</span>
                    )}

                    <EditIcon className={classes.fieldsIcon} onClick={() => handleSubdomainEditChange(true)} />
                  </div>
                )}
              </div>
            )}
          </Grid>

          {workspace?.productType !== WorkspaceProductType.TechQA.value && (
            <Grid item style={{ marginTop: '8px' }}>
              <div style={{ textAlign: 'right', width: '300px' }}>
                <Tooltip
                  interactive
                  placement="top-end"
                  title={
                    <Typography className={`${classes.clinicUploadTooltip}`}>
                      {canRequestBeforePay ? canRequestBeforePayCopy.allowed : canRequestBeforePayCopy.notAllowed}
                    </Typography>
                  }>
                  <Chip
                    avatar={<ErrorOutline className={canRequestBeforePay ? classes.alertIconGreen : classes.alertIconWhite} />}
                    onDelete={canRequestBeforePay}
                    onClick={handleCanRequestBeforePayClick}
                    deleteIcon={canRequestBeforePay && <Done className={classes.alertIconWhite} />}
                    clickable
                    color="primary"
                    variant={canRequestBeforePay ? 'default' : 'outlined'}
                    size="medium"
                    label={`Can request before pay ${canRequestBeforePay ? '' : 'not-'}allowed`}
                    style={{ marginTop: '8px' }}
                  />
                </Tooltip>

                <Tooltip
                  interactive
                  placement="top-end"
                  title={<Typography className={`${classes.clinicUploadTooltip}`}>{workspaceCreatorMode ? creatorModeCopy.allowed : creatorModeCopy.notAllowed}</Typography>}>
                  <Chip
                    avatar={<ErrorOutline className={workspaceCreatorMode ? classes.alertIconGreen : classes.alertIconWhite} />}
                    onDelete={workspaceCreatorMode}
                    onClick={handleCreatorModeChange}
                    deleteIcon={workspaceCreatorMode && <Done className={classes.alertIconWhite} />}
                    clickable
                    color="primary"
                    variant={workspaceCreatorMode ? 'default' : 'outlined'}
                    size="medium"
                    label={`Creator mode ${workspaceCreatorMode ? 'enabled' : 'disabled'}`}
                    style={{ marginTop: '8px' }}
                  />
                </Tooltip>

                <Tooltip
                  interactive
                  placement="top-end"
                  title={
                    <Typography className={`${classes.clinicUploadTooltip}`}>
                      {workspaceRadiologyReportRequired ? radiologyReportRequiredCopy.allowed : radiologyReportRequiredCopy.notAllowed}
                    </Typography>
                  }>
                  <Chip
                    avatar={<ErrorOutline className={workspaceRadiologyReportRequired ? classes.alertIconGreen : classes.alertIconWhite} />}
                    onDelete={workspaceRadiologyReportRequired}
                    onClick={handleRadiologyReportRequiredChange}
                    deleteIcon={workspaceRadiologyReportRequired && <Done className={classes.alertIconWhite} />}
                    clickable
                    color="primary"
                    variant={workspaceRadiologyReportRequired ? 'default' : 'outlined'}
                    size="medium"
                    label={`Radiology report ${workspaceRadiologyReportRequired ? 'required' : 'not required'}`}
                    style={{ marginTop: '8px' }}
                  />
                </Tooltip>

                <Tooltip
                  interactive
                  placement="top-end"
                  title={
                    <Typography className={`${classes.clinicUploadTooltip}`}>
                      {sendPlacedOrdersToAdmins ? sendEmailsToAdminsCopy.send : sendEmailsToAdminsCopy.doNotSend}
                    </Typography>
                  }>
                  <Chip
                    avatar={<ErrorOutline className={sendPlacedOrdersToAdmins ? classes.alertIconGreen : classes.alertIconWhite} />}
                    onDelete={sendPlacedOrdersToAdmins}
                    onClick={handleSendPlacedOrdersToAdminsChange}
                    deleteIcon={sendPlacedOrdersToAdmins && <Done className={classes.alertIconWhite} />}
                    clickable
                    color="primary"
                    variant={sendPlacedOrdersToAdmins ? 'default' : 'outlined'}
                    size="medium"
                    label={` ${sendPlacedOrdersToAdmins ? 'Send' : 'Do not send'} Placed Orders to Admins`}
                    style={{ marginTop: '8px' }}
                  />
                </Tooltip>
              </div>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item container>
        {workspace?.productType !== WorkspaceProductType.TechQA.value && (
          <Grid container justifyContent="flex-end">
            <Grid item style={{ flexGrow: 1, marginTop: '8px', paddingRight: '8px', textAlign: 'left' }}>
              <FormControl fullWidth>
                <p className={classes.typographyTitle}>Main file type</p>
                <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'nowrap' }}>
                  <Select
                    onChange={handleUpdateMainFileType}
                    autoFocus={false}
                    className={`bold-icon no-background-focus ${classes.selectContainer}`}
                    style={{ flexGrow: 1 }}
                    name="mainFileType"
                    value={mainFileType || ''}
                    variant="outlined"
                    size="small">
                    <MenuItem value={WorkspaceMainFileType.Dicom.value}>{WorkspaceMainFileType.Dicom.label}</MenuItem>
                    <MenuItem value={WorkspaceMainFileType.Any.value}>{WorkspaceMainFileType.Any.label}</MenuItem>
                  </Select>
                </div>
              </FormControl>
            </Grid>

            <Grid item style={{ flexGrow: 1, marginTop: '8px', textAlign: 'left' }}>
              <FormControl fullWidth>
                <p className={classes.typographyTitle}>Discount Type</p>
                <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'nowrap' }}>
                  <Select
                    onChange={handleUpdateDiscount}
                    autoFocus={false}
                    className={`bold-icon no-background-focus ${classes.selectContainer}`}
                    style={{ flexGrow: 1 }}
                    name="discountType"
                    value={workspaceDiscount?.id || ''}
                    variant="outlined"
                    size="small">
                    {discounts.map((discount) => (
                      <MenuItem value={discount.id}>
                        {discount.name} - {discount.code}
                      </MenuItem>
                    ))}
                  </Select>

                  {workspaceDiscount?.id ? (
                    <IconButton onClick={handleRemoveDiscount} size="small">
                      <CloseIcon />
                    </IconButton>
                  ) : null}
                </div>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Grid>

      <Grid item container style={{ marginBottom: '16px' }}>
        <Typography variant="h6" align="left">
          Administrators
        </Typography>

        <Table columns={columns} data={administrators || []} options={tableOptions} ref={tableRef} />

        <Button color="primary" style={{ marginTop: '0px', width: '200px' }} variant="contained" size="medium" onClick={onAddAdmin}>
          Add Administrator
        </Button>
      </Grid>
    </Grid>
  );
};

export default Administrators;
